import { Injectable } from '@angular/core';

import {
  DataService as CoreDataService,
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  PersistObjectsModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { Address } from '@barthauer/custom/xmt-models';
import { MailAndWebAddress, PhoneNumber } from '@barthauer/custom/xmt-models';
import { map, Observable, of, switchMap } from 'rxjs';
import { DataService, ObjectKeys } from '../../services/data.service';
import { UserRightPopupService } from '../../user-right/user-right-popup/user-right-popup.service';
import { ROLES } from '../../models/enumeration-list.model';

import { UserRolePermissionViewModel } from '../../view-models/user-role-permission.view-model';
import { GUID } from '@barthauer/abc/core';
import {
  PERMISSION_ADMIN,
  PERMISSION_CORE_TEAM,
  PERMISSION_MEASURE_PARTICIPANT,
  PERMISSION_MEASURE_RESPONSIBLE,
  PERMISSION_STEERING_COMMITTEE,
  PERMISSION_STRATEGIE_TEAM,
  PERMISSION_SUBPROJECT_DEPUTY,
  PERMISSION_SUBPROJECT_RESPONSIBLE,
} from '../../shared/permission-config/role-permission.config';

export const ROLE_PERMISSION_MAP = {
  [ROLES.Admin]: PERMISSION_ADMIN,
  [ROLES.CoreTeam]: PERMISSION_CORE_TEAM,
  [ROLES.MeasureManager]: PERMISSION_MEASURE_RESPONSIBLE,
  [ROLES.MeasureParticipant]: PERMISSION_MEASURE_PARTICIPANT,
  [ROLES.STRATEGIETEM]: PERMISSION_STRATEGIE_TEAM,
  [ROLES.SteeringCommittee]: PERMISSION_STEERING_COMMITTEE,
  [ROLES.SubprojectDeputy]: PERMISSION_SUBPROJECT_DEPUTY,
  [ROLES.SubprojectResponsible]: PERMISSION_SUBPROJECT_RESPONSIBLE,
};
@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  public isUpdating: boolean = false;
  public companyColumns: QueryColumn[] = [
    this.dataService.coreDataService.createQueryColumn('Id', 'Id'),
    this.dataService.coreDataService.createQueryColumn('Name', 'Name'),
    this.dataService.coreDataService.createQueryColumn('MatchCode', 'MatchCode'),
    this.dataService.coreDataService.createQueryColumn('Status', 'Status'),
    this.dataService.coreDataService.createQueryColumn('CompanyTitle', 'CompanyTitle'),
    this.dataService.coreDataService.createQueryColumn('Street', 'Street'),
    this.dataService.coreDataService.createQueryColumn('CustomerNumber', 'CustomerNumber'),
    this.dataService.coreDataService.createQueryColumn('BusinessLine', 'BusinessLine'),
    this.dataService.coreDataService.createQueryColumn('HouseNumber', 'HouseNumber'),
    this.dataService.coreDataService.createQueryColumn('ZIP', 'ZIP'),
    this.dataService.coreDataService.createQueryColumn('Country', 'Country'),
    this.dataService.coreDataService.createQueryColumn('City', 'City'),
    this.dataService.coreDataService.createQueryColumn(
      'SysDateInsert',
      'SysDateInsert',
      QueryColumnSortOrder.Descending,
    ),
    this.dataService.coreDataService.createQueryColumn(
      'SysTimeInsert',
      'SysTimeInsert',
      QueryColumnSortOrder.Descending,
    ),
    this.dataService.coreDataService.createQueryColumn(
      "Exists(Type(BoxDetail)[Box.ParentId = $CurrentUserId AND Box.Designation = 'Favoriten' AND ParentId = ^.Id])",
      'IsFavorite',
    ),
    this.dataService.coreDataService.createQueryColumn(
      "Type(BoxDetail)[Box.ParentId = $CurrentUserId AND Box.Designation = 'Favoriten' AND ParentId = ^.Id].Id",
      'BoxDetailId',
    ),
  ];

  constructor(
    private dataService: DataService,
    private persistenceService: PersistenceService,
    private userRightService: UserRightPopupService,
  ) {}

  public prepareCompanyColumns(oPath?: string) {
    const address: Query = new Query();
    address.ObjectType = ObjectKeys.ADDRESS;
    address.Columns = this.companyColumns;

    if (oPath) address.OPath = oPath;

    const mailAddressColumns = [
      this.dataService.coreDataService.createQueryColumn('Id', 'Id'),
      this.dataService.coreDataService.createQueryColumn('MainAdress', 'MainAddress'),
      this.dataService.coreDataService.createQueryColumn('Value', 'Value'),
      this.dataService.coreDataService.createQueryColumn('Type', 'Type'),
    ];

    const mailAddress: Query = new Query();
    mailAddress.ObjectType = ObjectKeys.MAILADDRESS;
    mailAddress.Name = 'MailAddress';
    mailAddress.OPath = 'MailAddresses';
    mailAddress.Columns = mailAddressColumns;

    const phoneNumberColumns: Array<QueryColumn> = [
      this.dataService.coreDataService.createQueryColumn('Id', 'Id'),
      this.dataService.coreDataService.createQueryColumn('Designation', 'Designation'),
      this.dataService.coreDataService.createQueryColumn('CountryCode', 'CountryCode'),
      this.dataService.coreDataService.createQueryColumn('LocalAreaCode', 'LocalAreaCode'),
      this.dataService.coreDataService.createQueryColumn('Mediation', 'Mediation'),
      this.dataService.coreDataService.createQueryColumn('Number', 'Number'),
      this.dataService.coreDataService.createQueryColumn('Show', 'Show'),
      this.dataService.coreDataService.createQueryColumn('Type', 'Type'),
      this.dataService.coreDataService.createQueryColumn('ParentId', 'ParentId'),
      this.dataService.coreDataService.createQueryColumn(
        "Concat(CaseTrue(CountryCode LIKE '+%', CountryCode, Concat('+', CountryCode)), ' ', LocalAreaCode, ' ', Number, CaseTrue(Not(IsNull(Mediation)), Concat('-', Mediation), ''))",
        'FullNumber',
      ),
    ];

    const phone: Query = new Query();
    phone.ObjectType = ObjectKeys.PHONENUMBER;
    phone.Name = 'PhoneNumbers';
    phone.OPath = 'PhoneNumbers[Type=0]';
    phone.Columns = phoneNumberColumns;

    address.ObjectQueries = [mailAddress, phone];
    const companyDetailsColumns = new QueryObjectsModel();
    companyDetailsColumns.ObjectQueries = [address];
    return companyDetailsColumns;
  }

  public persistCompany(company: Address): Observable<any> {
    const companyColumns: TargetColumnValue[] = [
      { Name: 'Name', Value: company.Name },
      { Name: 'Status', Value: company.Status },
      { Name: 'CustomerNumber', Value: company.CustomerNumber },
      { Name: 'Country', Value: <string>company.Country },
      { Name: 'City', Value: company.City },
      { Name: 'Street', Value: company.Street },
      { Name: 'MatchCode', Value: company.MatchCode },
      { Name: 'HouseNumber', Value: company.HouseNumber },
      { Name: 'ZIP', Value: company.ZIP },
      { Name: 'BusinessLine', Value: company.BusinessLine },
    ];

    const companyPersistQuery: TargetObjectData = new TargetObjectData();
    companyPersistQuery.ObjectKey = new ObjectKey();
    companyPersistQuery.ObjectKey.ObjectType = ObjectKeys.ADDRESS;
    companyPersistQuery.TargetColumns = companyColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = companyPersistQuery;

    const isInsert = !company.Id;

    if (!isInsert) {
      companyPersistQuery.Mode = PersistMode.Update;
      companyPersistQuery.ObjectKey.Id = company.Id;
    } else {
      companyPersistQuery.Mode = PersistMode.Insert;
    }

    return this.persistenceService.executePersistObjectQuery(persistObject).pipe(
      switchMap((result) => {
        if (isInsert) {
          return this.initializeCompanyPermissions(result['Id']).pipe(map(() => result));
        }

        // check if permissions exist, create them if not
        return this.prepareCompanyPermissions(company.Id).pipe(
          switchMap((permissions) => {
            if (!this.checkCompanyHasPermissions(permissions)) {
              return this.initializeCompanyPermissions(company.Id).pipe(map(() => result));
            }
            return of(result);
          }),
        );
      }),
    );
  }
  public persistPhoneNumber(phone: PhoneNumber, companyId: string, phoneFaxId?: string): Observable<any> {
    const numberColumns: TargetColumnValue[] = [
      { Name: 'CountryCode', Value: phone.CountryCode },
      { Name: 'LocalAreaCode', Value: phone.LocalAreaCode },
      { Name: 'Number', Value: phone.Number },
      { Name: 'Mediation', Value: phone.Mediation },
    ];
    const numberPersistQuery: TargetObjectData = new TargetObjectData();
    numberPersistQuery.ObjectKey = new ObjectKey();
    numberPersistQuery.ObjectKey.ObjectType = ObjectKeys.PHONENUMBER;
    numberPersistQuery.TargetColumns = numberColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = numberPersistQuery;
    if (phoneFaxId) {
      numberPersistQuery.Mode = PersistMode.Update;
      numberPersistQuery.ObjectKey.Id = phoneFaxId;
    } else {
      numberPersistQuery.Mode = PersistMode.Insert;
      numberColumns.push(
        { Name: 'ParentId', Value: companyId },
        { Name: 'Type', Value: phone.Type },
        { Name: 'Show', Value: true },
      );
    }

    return this.persistenceService.executePersistObjectQuery(persistObject);
  }

  public persistMailAddress(email: MailAndWebAddress, companyId: string, emailId?: string): Observable<any> {
    const mailColumns: TargetColumnValue[] = [{ Name: 'Value', Value: email.Value }];
    const mailPersistQuery: TargetObjectData = new TargetObjectData();
    mailPersistQuery.ObjectKey = new ObjectKey();
    mailPersistQuery.ObjectKey.ObjectType = ObjectKeys.MAILADDRESS;
    mailPersistQuery.TargetColumns = mailColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = mailPersistQuery;
    if (emailId) {
      mailPersistQuery.Mode = PersistMode.Update;
      mailPersistQuery.ObjectKey.Id = emailId;
    } else {
      mailPersistQuery.Mode = PersistMode.Insert;
      mailColumns.push({ Name: 'MainAdress', Value: true }, { Name: 'ParentId', Value: companyId });
    }
    return this.persistenceService.executePersistObjectQuery(persistObject);
  }
  public initializeCompanyPermissions(companyId: string) {
    return this.createPermissionsForNewCompany(companyId);
  }

  public prepareCompanyPermissions(companyId: string): Observable<{ Id: string }[]> {
    const opath = `IdRefAddress = '${companyId}'`;
    const permissionColumns: QueryColumn[] = [this.dataService.coreDataService.createQueryColumn('Id', 'Id')];

    return this.dataService.readObjects(ObjectKeys.USER_ROLE_PERMISSION, permissionColumns, opath);
  }
  public checkCompanyHasPermissions(permissionsIds: { Id: string }[]): boolean {
    return Array.isArray(permissionsIds) && permissionsIds.length > 0;
  }
  public deleteObjects(objectKey: ObjectKeys, ids: string[]): Observable<any> {
    if (!ids?.length) {
      return of(null);
    }

    const query = new PersistObjectsModel();
    const objectsPersistQuery: TargetObjectData[] = [];

    ids.forEach((id) => {
      const target = new TargetObjectData();
      target.Mode = PersistMode.Delete;
      target.ObjectKey = new ObjectKey();
      target.ObjectKey.Id = id;
      target.ObjectKey.ObjectType = objectKey;

      objectsPersistQuery.push(target);
    });

    query.Objects = [objectsPersistQuery];

    return this.dataService.coreDataService.executePersistObjectsQuery(query);
  }

  public createPermissionsForNewCompany(companyId: string) {
    const allPermissions = Object.entries(ROLE_PERMISSION_MAP).flatMap(([roleName, template]) => {
      return template.map((module) => this.cloneModuleForCompany(module, companyId, roleName));
    });

    this.userRightService.resetModulesMap();

    this.userRightService.buildModulesMap(allPermissions);
    return this.userRightService.persistUserRolePermissions();
  }

  public cloneModuleForCompany(
    module: UserRolePermissionViewModel,
    companyId: string,
    roleName: string,
    parentId?: string,
  ): UserRolePermissionViewModel {
    const cloned = new UserRolePermissionViewModel(
      module.Context,
      module.CanView,
      module.CanCreate,
      module.CanDelete,
      module.CanUpdate,
      [],
    );

    cloned.Id = GUID.newGUID();
    cloned.IdRefAddress = companyId;
    cloned.Name = roleName;
    cloned.isNew = true;
    if (parentId) cloned.IdRefParentURPermission = parentId;

    cloned.ChildUserRolePermissions =
      module.ChildUserRolePermissions?.map((child) =>
        this.cloneModuleForCompany(child, companyId, roleName, cloned.Id),
      ) || [];

    return cloned;
  }
}
